import React, { useEffect, useMemo, useState } from "react";
import ReactDOM from "react-dom";

import Notice from "../../Notice";
import SkeletonBlock from "../../common/SkeletonBlock";
import { useDateTimeFormat } from "../../../hooks/useDateTimeFormat";
import {
  buildDemographicFieldHistory,
  buildDemographicSections,
  getDemographicFieldLabel,
} from "../../../utils/patients/patientDemographics";

function readErrorMessage(error, t) {
  return (
    error?.response?.data?.error?.message ||
    error?.response?.data?.error ||
    error?.message ||
    t(
      "profilePage.demographicsError",
      "Could not load these {{subjectLower}} details.",
    )
  );
}

function DemographicRow({ row, onOpenHistory, t }) {
  const changeLabel =
    row.changeCount > 0
      ? t("profilePage.demographicsChangeCount", {
          count: row.changeCount,
          defaultValue: "{{count}} changes",
        })
      : null;

  return (
    <tr className="border-b border-greyscale-1 bg-container transition-colors last:border-b-0">
      <td className="px-3 py-3 text-sm text-greyscale-3">{row.label}</td>

      <td
        className={`px-3 py-3 text-sm font-medium ${
          row.value ? "text-typography" : "text-greyscale-3"
        }`}
      >
        {row.value || t("profilePage.demographicsNotProvided", "Not provided")}
      </td>

      <td className="px-3 py-3 text-center text-sm text-greyscale-3">
        {changeLabel}
      </td>

      <td className="px-3 py-3 text-center">
        {row.hasHistory ? (
          <button
            type="button"
            onClick={() => onOpenHistory(row.key)}
            className="inline-flex h-8 w-8 items-center justify-center rounded-lg text-typography transition-colors hover:bg-greyscale-1 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary"
            aria-label={t("profilePage.demographicsOpenHistoryLabel", {
              field: row.label,
              defaultValue: "View past entries for {{field}}",
            })}
          >
            <span
              className="material-symbols-outlined text-xl"
              aria-hidden="true"
            >
              more_vert
            </span>
          </button>
        ) : null}
      </td>
    </tr>
  );
}

function DemographicSection({ section, onOpenHistory, t }) {
  return (
    <section className="card flex flex-col overflow-hidden">
      <div className="border-b border-greyscale-1 px-3 py-3">
        <h3 className="text-sm font-semibold uppercase text-greyscale-3">
          {section.title}
        </h3>
      </div>

      <div className="overflow-x-auto">
        <table className="w-full min-w-[520px] table-fixed border-collapse">
          <colgroup>
            <col className="w-[34%]" />
            <col />
            <col className="w-28" />
            <col className="w-20" />
          </colgroup>

          <thead>
            <tr className="border-b border-greyscale-1 text-sm text-typography">
              <th scope="col" className="px-3 py-3 text-left font-medium">
                {t("profilePage.demographicsFieldHeader", "Field")}
              </th>
              <th scope="col" className="px-3 py-3 text-left font-medium">
                {t("profilePage.demographicsValueHeader", "Value")}
              </th>
              <th scope="col" className="px-3 py-3 text-center font-medium">
                {t("profilePage.demographicsChangeHeader", "Changes")}
              </th>
              <th scope="col" className="px-3 py-3 text-center font-medium">
                {t("profilePage.actionHeader", "Actions")}
              </th>
            </tr>
          </thead>

          <tbody>
            {section.rows.map((row) => (
              <DemographicRow
                key={row.key}
                row={row}
                onOpenHistory={onOpenHistory}
                t={t}
              />
            ))}
          </tbody>
        </table>
      </div>
    </section>
  );
}

function DemographicHistoryPanel({
  fieldKey,
  historyFields,
  historyLoading,
  historyError,
  historyTruncated,
  onBack,
  formatDateTime,
  t,
}) {
  const entries = useMemo(
    () =>
      buildDemographicFieldHistory(historyFields, fieldKey, t, {
        formatDate: (value) => formatDateTime(value).date,
      }),
    [fieldKey, formatDateTime, historyFields, t],
  );

  return (
    <div className="flex flex-col gap-4">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-2">
          <button
            type="button"
            className="small-outlined-button !min-h-0 !px-3 !py-1.5"
            onClick={onBack}
          >
            {t("profilePage.demographicsBackToDetails", "Back to details")}
          </button>
          <h3 className="text-lg font-semibold text-typography">
            {getDemographicFieldLabel(fieldKey, t)}
          </h3>
        </div>
      </div>

      {historyLoading ? (
        <div className="flex flex-col gap-2">
          <SkeletonBlock className="h-12 w-full rounded-lg" />
          <SkeletonBlock className="h-12 w-full rounded-lg" />
          <SkeletonBlock className="h-12 w-full rounded-lg" />
        </div>
      ) : historyError ? (
        <Notice
          tone="error"
          role="alert"
          message={readErrorMessage(historyError, t)}
        />
      ) : entries.length === 0 ? (
        <p className="rounded-lg border border-greyscale-1 bg-container px-3 py-10 text-center text-sm text-greyscale-3">
          {t(
            "profilePage.demographicsNoHistory",
            "No past entries have been recorded for this field.",
          )}
        </p>
      ) : (
        <>
          <ol className="flex flex-col gap-2">
            {entries.map((entry) => {
              const recorded = entry.recordedAt
                ? formatDateTime(entry.recordedAt)
                : null;

              return (
                <li
                  key={entry.key}
                  className="flex flex-wrap items-center justify-between gap-3 rounded-lg border border-greyscale-1 bg-container px-3 py-3"
                >
                  <div className="min-w-0">
                    <p className="text-base font-semibold text-typography">
                      {entry.value ||
                        t("profilePage.demographicsNotProvided", "Not provided")}
                    </p>
                    <p className="mt-0.5 text-xs text-greyscale-3">
                      {recorded
                        ? [recorded.date, recorded.time].filter(Boolean).join(" ")
                        : t(
                            "profilePage.demographicsUnknownDate",
                            "Date not recorded",
                          )}
                    </p>
                  </div>

                  {entry.isCurrent ? (
                    <span className="shrink-0 rounded-full bg-primary px-2.5 py-1 text-xs font-semibold text-greyscale-1">
                      {t("profilePage.demographicsCurrentValue", "Current")}
                    </span>
                  ) : null}
                </li>
              );
            })}
          </ol>

          {historyTruncated ? (
            <Notice
              message={t(
                "profilePage.demographicsHistoryTruncated",
                "Only the most recent entries are shown for this {{subjectLower}}.",
              )}
            />
          ) : null}
        </>
      )}
    </div>
  );
}

/**
 * Clinician-facing patient details overlay.
 *
 * Shows the demographic profile the patient entered during onboarding (and can
 * still edit), grouped for reading. Any field the patient has answered opens a
 * timeline of its past entries, which is what makes a changing value such as
 * weight readable rather than just "whatever it says today".
 */
export default function PatientDemographicsOverlay({
  open,
  onClose,
  subjectLabel,
  clinicStudySummary,
  subject,
  profile,
  historyFields,
  historyTruncated = false,
  loading = false,
  historyLoading = false,
  error = null,
  historyError = null,
  onRetry,
  t,
}) {
  const { formatDateTime } = useDateTimeFormat();
  const [selectedFieldKey, setSelectedFieldKey] = useState(null);

  // Reopening the overlay should always land on the summary, never on whichever
  // field happened to be open when it was last closed.
  useEffect(() => {
    if (!open) {
      setSelectedFieldKey(null);
    }
  }, [open]);

  const sections = useMemo(
    () =>
      buildDemographicSections(profile, t, {
        historyFields: historyLoading ? null : historyFields,
        formatDate: (value) => formatDateTime(value).date,
      }),
    [formatDateTime, historyFields, historyLoading, profile, t],
  );

  const hasAnyValue = sections.some((section) =>
    section.rows.some((row) => Boolean(row.value)),
  );

  if (!open) {
    return null;
  }

  return ReactDOM.createPortal(
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 sm:p-6">
      <button
        type="button"
        aria-label={t(
          "profilePage.demographicsCloseLabel",
          "Close {{subjectLower}} details",
        )}
        className="absolute inset-0 cursor-default bg-black/45 backdrop-blur-sm"
        onClick={onClose}
      />

      <div className="relative z-10 flex max-h-[92vh] w-[96vw] max-w-4xl flex-col overflow-hidden rounded-2xl border border-greyscale-1 bg-background shadow-2xl">
        <div className="flex flex-wrap items-start justify-between gap-4 border-b border-greyscale-1 p-4">
          <div className="min-w-0">
            <h2 className="text-2xl font-semibold text-typography">
              {t("profilePage.demographicsTitle", "{{subject}} details")}
            </h2>
            <p className="mt-1 text-sm text-greyscale-3">
              {t(
                "profilePage.demographicsSubtitle",
                "The details this {{subjectLower}} entered. Select a field to see its past entries.",
              )}
            </p>
            <p className="mt-2 text-sm text-greyscale-3">
              {subjectLabel || subject?.subjectId || "--"}
              {clinicStudySummary ? ` · ${clinicStudySummary}` : ""}
            </p>
          </div>

          <button
            type="button"
            className="outlined-button !min-h-0 !px-4 !py-2 !text-sm"
            onClick={onClose}
          >
            {t("profilePage.closeOverlayButton", "Close")}
          </button>
        </div>

        <div className="flex-1 overflow-y-auto p-4">
          {loading ? (
            <div className="flex flex-col gap-4">
              <SkeletonBlock className="h-56 w-full rounded-xl" />
              <SkeletonBlock className="h-44 w-full rounded-xl" />
              <SkeletonBlock className="h-52 w-full rounded-xl" />
            </div>
          ) : error ? (
            <div className="flex flex-col items-start gap-3">
              <Notice
                tone="error"
                role="alert"
                message={readErrorMessage(error, t)}
              />
              {onRetry ? (
                <button
                  type="button"
                  className="small-outlined-button !min-h-0 !px-3 !py-1.5"
                  onClick={onRetry}
                >
                  {t("profilePage.demographicsRetry", "Try again")}
                </button>
              ) : null}
            </div>
          ) : selectedFieldKey ? (
            <DemographicHistoryPanel
              fieldKey={selectedFieldKey}
              historyFields={historyFields}
              historyLoading={historyLoading}
              historyError={historyError}
              historyTruncated={historyTruncated}
              onBack={() => setSelectedFieldKey(null)}
              formatDateTime={formatDateTime}
              t={t}
            />
          ) : !hasAnyValue ? (
            <p className="rounded-lg border border-greyscale-1 bg-container px-3 py-10 text-center text-sm text-greyscale-3">
              {t(
                "profilePage.demographicsEmpty",
                "This {{subjectLower}} has not completed their profile yet.",
              )}
            </p>
          ) : (
            <div className="flex flex-col gap-4">
              {sections.map((section) => (
                <DemographicSection
                  key={section.key}
                  section={section}
                  onOpenHistory={setSelectedFieldKey}
                  t={t}
                />
              ))}
            </div>
          )}
        </div>
      </div>
    </div>,
    document.body,
  );
}
